import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MessageCircle, MapPin, Phone, Mail, Star, Zap, Gauge, TrendingUp, Wrench, Leaf } from 'lucide-react';
import { testimonials } from '../mock';
import CalculatorWidget from '../components/CalculatorWidget';
import { useSeo } from '../hooks/useSeo';
import { seo, LOGO_URL, faqIzmirTr } from '../seoConfig';
import LegalLinks from '../components/LegalLinks';

const WA_URL = 'https://wa.me/905327866635?text=Merhaba%2C%20%C4%B0zmir%20Powerlab%20chip%20tuning%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum';

const IzmirChipTuningTR = () => {
  useSeo(seo.izmirTr);

  const izmirStats = [
    { number: '40+', label: 'Dünya Genelinde Bayi' },
    { number: '20+', label: 'Ülke' },
    { number: '300.000+', label: 'İşlenmiş Araç Dosyası' },
    { number: '2012\'den Beri', label: 'Aktif (Estonya 2019)' },
  ];

  const services = [
    { icon: Zap, title: 'Stage 1 / 2 / 3 Yazılım', desc: 'Günlük kullanımdan tam yarış sürümüne kadar araca özel ECU yazılımı.' },
    { icon: Wrench, title: 'DPF / EGR / AdBlue', desc: 'Dizel emisyon sistem çözümleri; sorunlu araçlarda güvenli yazılım desteği.' },
    { icon: Leaf, title: 'E85 Bioetanol Dönüşüm', desc: 'Daha yeşil, daha ucuz yakıt ve daha çok güç. Tam flex-fuel kalibrasyonu.' },
    { icon: Gauge, title: 'Dyno Test', desc: 'Her yazılım kendi dyno test cihazımızda doğrulanır — gerçek güç, güvenli sınırlar.' },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Powerlab Works" className="h-14 w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-300 hover:text-[#FF6600] transition-colors">Ana Sayfa</Link>
              <Link to="/calculator" className="text-gray-300 hover:text-[#FF6600] transition-colors">Güç Hesaplayıcı</Link>
              <Link to="/faq" className="text-gray-300 hover:text-[#FF6600] transition-colors">SSS</Link>
              <Link to="/izmir-chip-tuning" className="text-gray-300 hover:text-[#FF6600] transition-colors" hrefLang="en">EN</Link>
            </nav>
            <div className="flex items-center space-x-3">
              <Button onClick={() => window.open(WA_URL, '_blank')} className="hidden sm:inline-flex bg-[#25D366] hover:bg-[#1DA851] text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=%C4%B0zmir%20Randevu%20Talebi&body=Merhaba%2C%20%C4%B0zmir%20lokasyonunuz%20i%C3%A7in%20chip%20tuning%20randevusu%20almak%20istiyorum.'} className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                Randevu Al
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-black via-[#0a0a0a] to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <span className="inline-block text-[#FF6600] font-semibold text-sm uppercase tracking-widest mb-4">İzmir • Bornova</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            İzmir'in Chip Tuning ve<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6600] to-[#E55A00]">ECU Yazılım Merkezi</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            300.000+ işlenmiş araç dosyası, 40+ dünya bayi ağı, 20+ ülke, 2012'den beri aktif (Estonya'da 2019'da resmi kuruluş). Bornova atölyemizde dyno test edilmiş, güvenli ve geri alınabilir ECU yazılımları.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-[#FF6600] to-[#E55A00] hover:from-[#E55A00] hover:to-[#CC4A00] text-white px-8 py-6 text-lg">
                Ücretsiz Hesapla <TrendingUp className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button onClick={() => window.open(WA_URL, '_blank')} className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-6 text-lg">
              <MessageCircle className="mr-2 w-5 h-5" /> WhatsApp'tan Yaz
            </Button>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-12 bg-[#0a0a0a] border-y border-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {izmirStats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FF6600] mb-2">{s.number}</div>
                <div className="text-sm md:text-base text-gray-400 uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">İzmir'de Hizmetlerimiz</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Motorunuzun ihtiyacı olan her şey</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="bg-[#1a1a1a] border-gray-800 hover:border-[#FF6600] transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#FF6600]/20 rounded-full flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#FF6600]" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Embedded Calculator */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Hemen deneyin</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Aracınızın gerçek potansiyelini keşfedin</h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">Gerçek tuning veritabanımızdan anlık Stage 1 kazanç tahminleri.</p>
          </div>
          <CalculatorWidget labels={{
            cardTitle: 'Araç Performans Hesaplayıcı',
            subtitle: (n) => `🎯 ${n} marka • 14.210 motor • 13.164 performans kaydı`,
            step1: '1. Marka Seçin *',
            step2: '2. Model Seçin *',
            step3: '3. Yıl / Nesil Seçin *',
            step4: '4. Motor Seçin *',
            chooseBrand: 'Marka seçin',
            loadingBrands: 'Markalar yükleniyor...',
            chooseModel: 'Model seçin',
            selectBrandFirst: 'Önce marka seçin',
            noModels: 'Model bulunamadı',
            chooseYear: 'Yıl seçin',
            selectModelFirst: 'Önce model seçin',
            noGenerations: 'Nesil bulunamadı',
            chooseEngine: 'Motor seçin',
            selectGenFirst: 'Önce nesil seçin',
            noEngines: 'Motor bulunamadı',
            selectedLine: 'Seçim',
            calculate: 'Performans Kazancını Hesapla',
            loading: 'Performans verisi yükleniyor...',
            techSpecs: 'Teknik Özellikler',
            stage1Gains: 'Stage 1 Performans Kazançları',
            stockPerf: 'Orijinal Performans',
            stage1Tuned: 'Stage 1 Tuned',
            power: 'Güç',
            torque: 'Tork',
            powerGain: 'Güç Kazancı',
            torqueGain: 'Tork Kazancı',
            powerIncrease: 'Güç Artışı',
            torqueIncrease: 'Tork Artışı',
            gain: 'Kazanç',
            book: 'Randevu Al',
            quote: 'WhatsApp\'tan Teklif Al',
            another: '← Başka Araç Hesapla',
            fuelType: 'Yakıt Tipi',
            method: 'Yöntem',
            tuningType: 'Tuning Tipi',
            cylinderContent: 'Silindir Hacmi',
            compressionRatio: 'Sıkıştırma Oranı',
            boreStroke: 'Çap X Strok',
            engineNumber: 'Motor Numarası',
            engineEcu: 'Motor ECU',
            selectEngineFirst: 'Lütfen önce motor seçin',
            loadErrorTitle: 'Araç verisi yüklenemedi',
            loadErrorMsg: 'İnternet bağlantınızı kontrol edip tekrar deneyin.',
            retry: 'Tekrar dene',
            perfErrorMsg: 'Performans verisi yüklenemedi. Lütfen tekrar deneyin.',
          }} />
        </div>
      </section>

      {/* Location block */}
      <section id="konum" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start max-w-6xl mx-auto">
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Bizi ziyaret edin</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">Powerlab Works HQ Türkiye</h2>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Adres</p>
                    <p>Kazımdirik Mahallesi 283. Sokak No:3, Bornova / İzmir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Telefon / WhatsApp</p>
                    <a href="tel:+905327866635" className="hover:text-[#FF6600]">+90 532 786 66 35</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">E-posta</p>
                    <a href="mailto:info@powerlab.works" className="hover:text-[#FF6600]">info@powerlab.works</a>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button onClick={() => window.open(WA_URL, '_blank')} className="bg-[#25D366] hover:bg-[#1DA851] text-white">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                <Button onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=%C4%B0zmir%20Randevu%20Talebi'} className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                  Randevu Al
                </Button>
              </div>
            </div>
            <div className="relative w-full h-[400px] rounded-lg overflow-hidden border border-gray-800 shadow-2xl">
              <iframe
                title="Powerlab Works HQ Türkiye - Bornova, İzmir"
                src="https://www.google.com/maps?q=Kaz%C4%B1mdirik%20Mahallesi%20283.%20Sokak%20No%3A3%20Bornova%20%C4%B0zmir&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Müşterilerimiz Ne Diyor</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map(tr => (
              <Card key={tr.id} className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(tr.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#FF6600] text-[#FF6600]" />)}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{tr.text}"</p>
                  <div className="border-t border-gray-800 pt-4">
                    <p className="text-white font-semibold">{tr.name}</p>
                    <p className="text-gray-500 text-sm">{tr.vehicle}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ - AI-friendly, all answers visible for maximum extraction by AI search */}
      <section id="sss" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Sıkça Sorulan Sorular</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">İzmir chip tuning — cevaplar</h2>
            </div>
            <div className="space-y-4">
              {faqIzmirTr.map((item, i) => (
                <Card key={i} className="bg-[#1a1a1a] border-gray-800" data-testid={`faq-item-${i}`}>
                  <CardContent className="p-6">
                    <h3 className="text-white font-semibold text-lg mb-3">{item.q}</h3>
                    <p className="text-gray-300 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900 py-10">
        <div className="container mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Powerlab" className="h-12 w-auto object-contain mx-auto mb-4" />
          <p className="text-gray-500 text-sm mb-2">Powerlab Works OU • Estonya • Powerlab Works HQ Türkiye — Bornova, İzmir</p>
          <p className="text-gray-600 text-xs">© 2025 Powerlab Works OU. Tüm hakları saklıdır.</p>
          <LegalLinks lang="tr" className="mt-3" />
          <p className="mt-3">
            <Link to="/izmir-chip-tuning" className="text-[#FF6600] hover:underline text-sm" hrefLang="en">English version &rarr;</Link>
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 md:top-1/2 md:bottom-auto md:-translate-y-1/2 bg-[#25D366] hover:bg-[#1DA851] text-white p-4 rounded-full shadow-2xl z-40 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default IzmirChipTuningTR;
